import React, { useState, useEffect } from "react"
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  message,
  InputNumber,
  Spin,
  Typography,
} from "antd"
import ReportsService from "src/services/ReportService"
import SelectCountry from "src/components/Dropdown/SelectCountry"
import TableCustom from "src/components/Table/CustomTable"
import { SectionCard } from "src/pages/ADMIN/SuggestedWarehouse/Modal/styled"
import Button from "src/components/MyButton/Button"
import SelectProvider from "src/components/Dropdown/SelectProvider"
import dayjs from "dayjs"

const { Text } = Typography

const ModalDetailProposal = ({ open, onCancel, dataSelected, recordId }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState()
  const [tableData, setTableData] = useState([])
  const [totalQuantity, setTotalQuantity] = useState(0)
  const [grandTotal, setGrandTotal] = useState(0)
  const [finalTotal, setFinalTotal] = useState(0)

  useEffect(() => {
    if (open && recordId) {
      fetchDetailData()
    }
  }, [open, recordId])

  useEffect(() => {
    if (grandTotal) {
      calculateFinalPrice()
    }
  }, [grandTotal])

  const fetchDetailData = async () => {
    setLoading(true)
    try {
      const response = await ReportsService.getDetailBuy(recordId)
      const detail = response?.success || {}

      form.setFieldsValue({
        batch_number: detail.batch_number,
        country_id: detail.sim_groups?.[0]?.country_id,
        supplier_id: detail.sim_groups?.[0]?.supplier_id,
        discount_type:
          detail.discount_type === "percent"
            ? "Chiết khấu trước VAT"
            : "Chiết khấu sau VAT",
        discount: detail.discount,
        note: detail.note,
        total: detail.total_amount,
        total_before_vat: detail.total_before_vat,
        total_after_vat: detail.total_after_vat,
        vat: detail.vat,
        end_price: detail.end_price,
      })

      const simGroupsWithAmount = detail.sim_groups.map(item => {
        const unitPrice =
          parseFloat(Number(item.unit_price).toString().replace(/,/g, "")) || 0
        const count = parseFloat(item.count?.toString().replace(/,/g, "")) || 0
        const totalAmount = parseFloat((unitPrice * count).toFixed(2))

        // Xử lý chiết khấu
        let discountDisplay = ""
        let discountAmount = 0
        let discount = 0

        if (item.deployment_type === "percent") {
          const percent =
            parseFloat(item.discount_value.toString().replace(/,/g, "")) || 0
          discountAmount = parseFloat(
            ((totalAmount * percent) / 100).toFixed(2),
          )
          discountDisplay = `${percent}%`
        } else if (item.deployment_type === "fixed") {
          discount =
            parseFloat(item.discount_value.toString().replace(/,/g, "")) || 0
          discountAmount =
            parseFloat(item.discount_value.toString().replace(/,/g, "")) *
              count || 0
          discountDisplay = `${discount.toLocaleString()}`
        }

        const finalAmount = parseFloat(
          (totalAmount - discountAmount).toFixed(2),
        )
        const activationDeadline =
          item.activation_deadline && dayjs(item.activation_deadline).isValid()
            ? dayjs(item.activation_deadline).format("DD/MM/YYYY")
            : ""

        return {
          ...item,
          unit_price: unitPrice,
          total_amount: totalAmount.toFixed(2),
          discount_display: discountDisplay,
          discount_amount: discountAmount.toFixed(2),
          final_amount: finalAmount.toFixed(2),
          connection_fee: item.connection_fee
            ? parseFloat(item.connection_fee.toString().replace(/,/g, ""))
            : 0,
          sim_fee: item.sim_fee
            ? parseFloat(item.sim_fee.toString().replace(/,/g, ""))
            : 0,
          package_unit_price: item.package_unit_price
            ? parseFloat(item.package_unit_price.toString().replace(/,/g, ""))
            : 0,
          vat: item.vat ? parseFloat(item.vat.toString().replace(/,/g, "")) : 0,
          activation_deadline: activationDeadline,
          coverage_areas: item.carrier?.coverage_areas || [],
        }
      })

      setTableData(simGroupsWithAmount)

      // Tính tổng count
      const totalQuantity = simGroupsWithAmount.reduce(
        (sum, item) => sum + (parseFloat(item.count) || 0),
        0,
      )
      const totalAmount = simGroupsWithAmount.reduce(
        (sum, item) => sum + parseFloat(item.final_amount || 0),
        0,
      )

      setTotalQuantity(totalQuantity)
      setGrandTotal(Number(totalAmount.toFixed(2)))
      setData(detail)
    } catch (error) {
      console.error("Lỗi khi lấy chi tiết data:", error)
      message.error("Không thể tải chi tiết phiếu")
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: "Gói cước",
      dataIndex: "package_name",
      align: "center",
    },
    {
      title: "Nhà mạng",
      dataIndex: "carrier_name",
      align: "center",
    },
    {
      title: "Vùng phủ sóng",
      dataIndex: "coverage_areas",
      render: areas => {
        if (!Array.isArray(areas) || areas.length === 0) return "-"

        // Lấy danh sách title quốc gia
        const countryTitles = areas
          .map(area => area.country?.title)
          .filter(Boolean) // loại bỏ undefined/null

        // Xoá trùng
        const uniqueTitles = [...new Set(countryTitles)]

        // Nối chuỗi
        return uniqueTitles.join(", ")
      },
      align: "center",
    },
    {
      title: "Loại SIM",
      dataIndex: "type_sim",
      render: value => {
        if (value === "esim") return "E-SIM"
        if (value === "usim") return "SIM vật lý"
        return value
      },
      align: "center",
    },
    {
      title: "Local/Roaming",
      dataIndex: "scope",
      render: value => {
        if (value === "1") return "Local"
        if (value === "2") return "Roaming"
        return value
      },
      align: "center",
    },
    {
      title: "Loại hình SIM",
      dataIndex: "sim_type",
      render: value => {
        if (value === "3") return "Roaming"
        if (value === "2") return "Travel"
        if (value === "1") return "IoT"
        if (value === "4") return "eKYC"
        return value
      },
      align: "center",
    },
    {
      title: "Loại thanh toán",
      dataIndex: "payment_type",
      align: "center",
      render: value => {
        if (value === "1") return "Trả trước"
        if (value === "2") return "Trả sau"
        return value
      },
    },
    {
      title: "Thời gian phải kích hoạt",
      dataIndex: "activation_deadline",
      align: "center",
    },
    {
      title: "Phí hòa mạng",
      dataIndex: "connection_fee",
      render: text => <Text>{parseFloat(text || 0).toLocaleString()}</Text>,
      align: "right",
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
    },
    {
      title: "Phí SIM",
      dataIndex: "sim_fee",
      render: text => <Text>{parseFloat(text || 0).toLocaleString()}</Text>,
      align: "right",
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
    },
    {
      title: "Phí gói cước",
      dataIndex: "package_unit_price",
      render: text => <Text>{parseFloat(text || 0).toLocaleString()}</Text>,
      align: "right",
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
    },
    {
      title: "VAT",
      dataIndex: "vat",
      render: text => <Text>{parseFloat(text || 0).toLocaleString()}</Text>,
      align: "right",
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
    },
    {
      title: "Số lượng",
      dataIndex: "count",
      render: text => <Text>{text}</Text>,
      align: "center",
    },
    {
      title: "Tổng tiền",
      dataIndex: "total_amount",
      render: text => (
        <Text strong>{parseFloat(text || 0).toLocaleString()}</Text>
      ),
      align: "right",
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
    },
    // {
    //   title: "Chiết khấu",
    //   dataIndex: "discount_display",
    //   render: text => <Text strong>{text}</Text>,
    //   align: "center",
    // },
    // {
    //   title: "Tổng sau chiết khấu",
    //   dataIndex: "final_amount",
    //   render: text => (
    //     <Text strong>{parseFloat(text || 0).toLocaleString()}</Text>
    //   ),
    //   align: "right",
    //   onHeaderCell: () => ({
    //     style: { textAlign: "center" },
    //   }),
    // },
    {
      title: "Loại tiền tệ",
      dataIndex: "currency_type",
      align: "center",
      render: value => {
        if (value === "vnd") return "VND"
        if (value === "usd") return "USD"
        return value
      },
    },
  ]

  const calculateFinalPrice = () => {
    const discountType = form?.getFieldValue("discount_type")
    const discount = Number(form?.getFieldValue("discount") || 0)
    const total = Number(grandTotal || 0)
    const finalPrice =
      discountType === "percent"
        ? total * (1 - discount / 100)
        : total - discount
    setFinalTotal(finalPrice)
    return finalPrice.toFixed(2)
  }

  return (
    <Modal
      open={open}
      title="Chi tiết phiếu nhập kho"
      onCancel={() => {
        form?.resetFields()
        onCancel()
      }}
      width={1200}
      destroyOnClose
      footer={null}
    >
      {loading ? (
        <Spin tip="Đang tải dữ liệu..." size="large" />
      ) : (
        <Form form={form} layout="vertical">
          <SectionCard>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item name="batch_number" label="Số lô">
                  <Input placeholder="Số lô" disabled />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Nhà cung cấp" name="supplier_id">
                  <SelectProvider disabled={true} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <TableCustom
                  isPrimary
                  columns={columns}
                  dataSource={tableData}
                  pagination={false}
                  bordered
                  scroll={{ x: "max-content" }}
                  rowKey={record =>
                    `${record.carrier_id}_${record.sim_type}_${record.type_sim}`
                  }
                />
              </Col>
              <Col span={6}>
                <Form.Item label="Tổng số lượng sim">
                  <Text strong>{totalQuantity}</Text>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Tổng tiền trước VAT">
                  <Text strong>
                    {Number(
                      form.getFieldValue("total_before_vat"),
                    ).toLocaleString()}
                  </Text>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="VAT">
                  <Text strong>{Number(form.getFieldValue("vat"))} %</Text>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Tổng tiền sau VAT" name="total">
                  <Text strong>
                    {Number(
                      form.getFieldValue("total_after_vat"),
                    ).toLocaleString()}
                  </Text>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Loại chiết khấu">
                  <Text strong>{form.getFieldValue("discount_type")}</Text>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Chiết khấu">
                  <Text strong>{form.getFieldValue("discount")} %</Text>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Tổng tiền sau chiết khấu" name="total">
                  <Text strong>
                    {Number(
                      form.getFieldValue("end_price"),
                    ).toLocaleString()}
                  </Text>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="note" label="Ghi chú">
                  <Input.TextArea placeholder="Ghi chú" rows={3} disabled />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>
        </Form>
      )}
    </Modal>
  )
}

export default ModalDetailProposal
